/* System defined core library */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable, Observer, forkJoin, throwError } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';

/* User defined library/components */
import { BootstrapParams } from '../../../../bootstrap-params';
import { HttpService } from '../../../core/http.service';
import { ExamFeatureService } from '../../../shared/services/exam-feature.service';
import { TimerService } from '../../../shared/services/timer.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { MetricApiService } from '../../shared/services/metric-api.service';
import { PaymentApiService } from '../../shared/services/payment-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { Dictionary, TravserseModel } from '../../../shared/models/common/shared.model';
import { Address } from '../../../shared/models/scheduling/address.model';
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { BatteryConfig } from '../../../shared/models/scheduling/batteryConfig.model';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { BookingRequestReschedule } from '../../../shared/models/scheduling/bookingRequest.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Contact } from '../../../shared/models/scheduling/contact.model';
import { Country } from '../../../shared/models/scheduling/country.model';
import { Direction } from '../../../shared/models/scheduling/direction.model';
import { Exam } from '../../../shared/models/scheduling/exam.model';
import { Hold } from '../../../shared/models/scheduling/hold.model';
import { MetricDetails } from '../../../shared/models/scheduling/metrics.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { CreatePaymentSessionRequest, CreatePaymentSessionResponse, PaymentOrchestratorRequest, PaymentSession } from '../../../shared/models/scheduling/paymentSession.model';
import { Proctor } from '../../../shared/models/scheduling/proctor.model';
import { Program } from '../../../shared/models/scheduling/program.model';
import { QrRefund } from '../../../shared/models/scheduling/qr.model';
import { PaymentFeeDetail, RescheduleFee } from '../../../shared/models/scheduling/recheduleFee.model';
import { Session } from '../../../shared/models/scheduling/session.model';
import { SiteLocation } from '../../../shared/models/scheduling/siteLocation.model';
import { SiteSlot } from '../../../shared/models/scheduling/siteSlot.model';
import { Slot } from '../../../shared/models/scheduling/slot.model';
import { State } from '../../../shared/models/scheduling/state.model';
import { Test } from '../../../shared/models/scheduling/test.model';
import { Timezone } from '../../../shared/models/scheduling/timezone.model';

/* User defined enums/constants */
import { EligibilityErrorCodeMap, LanguageCultureCodes, QrPaymentCodes, SchedulingErrors, TargetFlow, TransactionType } from '../../../shared/constants/scheduling.constant';
import { CardFee, LogLevel } from '../../../shared/enums/scheduling.enum';


@Injectable()
export class SchedulingApiService {

  public paymentSessionData: PaymentSession;

  /* Constructor declaration with required injected services */
  constructor(
    private bootstrapParams: BootstrapParams,
    private httpService: HttpService,
    private metricApiService: MetricApiService,
    private paymentService: PaymentApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private timerService: TimerService,
    private utilityService: UtilityService
  ) {

  }

  getClients(): Observable<Client[]> {
    return new Observable((observer: Observer<Client[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/Clients?isPublished=true', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe((rArray) => {
          let clients: Client[] = rArray.map(rItem => {
            return new Client(
              rItem.id,
              rItem.code,
              rItem.uri,
              rItem.name,
              rItem.sso,
              rItem.requiresBiometricConsent,
              rItem.location
            );
          });
          observer.next(clients);
          observer.complete();
        });
    });
  }

  getPrograms(clientId: string): Observable<Program[]> {
    return new Observable((observer: Observer<Program[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/Programs?clientId=' + clientId + '&isPublished=true', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe((rArray) => {
          let programs: Program[] = rArray.map(rItem => {
            return {
              id: rItem.id,
              name: rItem.name,
              uri: rItem.uri,
              code: rItem.code,
              isCandidateCentric: rItem.isCandidateCentric,
            }
          });
          observer.next(programs);
          observer.complete();
        });
    });
  }

  getCountries(): Observable<Country[]> {
    return new Observable((observer: Observer<Country[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/countries', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe((rArray) => {
          const countries: Country[] = rArray.map(rItem => {
            return new Country(rItem);
          });
          observer.next(countries);
          observer.complete();
        });
    });
  }

  getProvinces(countryCode: string): Observable<State[]> {
    return new Observable((observer: Observer<State[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/countries/' + countryCode + '/Provinces', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe({
          next: (rArray) => {
            let states: State[] = rArray.map(rItem => {
              return new State(rItem.code, rItem.name, null, "39.27522", "-77.56876");
            });
            observer.next(states);
            observer.complete();
          },
          error: (err) => {
            let states: State[] = null;
            observer.next(states); //  Error handling in case of no data.
            observer.complete();
          }
        });
    });
  }

  getCountriesByExam(examId: string): Observable<Country[]> {
    return new Observable((observer: Observer<Country[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/exams/' + examId + '/countries', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe((rArray) => {
          const countries: Country[] = rArray.map(rItem => {
            return new Country(rItem);
          });

          const travserseModel: TravserseModel = new TravserseModel();
          travserseModel.filterByName = 'id';
          travserseModel.uri = this.bootstrapParams.rootApiUrl + 'api/v1/countries';
          travserseModel.onDemandLoading = false;

          this.utilityService.TraverseModel<Country>(countries, travserseModel, this.bootstrapParams.Token).subscribe((result2) => {
            observer.next(result2);
            observer.complete();
          });

        });
    });
  }

  getExams(programId: string): Observable<Exam[]> {

    return new Observable((observer: Observer<Exam[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/Exams/?programId=' + programId + '&isPublished=true', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe((rArray) => {
          let examinations: Exam[] = rArray.map(rItem => {
            return Exam.convertAPIObjectToExam(rItem)
          });
          examinations.sort(ExamFeatureService.compareExamsForSort);
          observer.next(examinations);
          observer.complete();
        });
    });
  }

  getExamDetails(examId: string): Observable<Exam> {
    return new Observable((observer: Observer<Exam>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/Exams/' + examId, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((examResult) => {
          observer.next(Exam.convertAPIObjectToExam(examResult));
          observer.complete();
        });
    });
  }

  getSlotBySite(href: any): Observable<SiteSlot> {
    let tmpSiteSlot: SiteSlot = new SiteSlot(null, null, null, null, null, null);
    return new Observable((observer: Observer<SiteSlot>) => {
      this.httpService.get(href, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((item: any) => {


          tmpSiteSlot.id = item.id;
          tmpSiteSlot.uri = item.uri;
          tmpSiteSlot.type = item.type;
          tmpSiteSlot.prefix = item.prefix;

          let address = new Address(
            item.location.address.addressLine,
            item.location.address.adminDistrict,
            item.location.address.country,
            item.location.address.formattedAddress,
            item.location.address.locality,
            item.location.address.postalCode
          );

          let tempDirection: Direction[] = [];
          item.location.directions.forEach((directid: any) => {
            tempDirection.push(new Direction(item.location.directions[directid].name,
              item.location.directions[directid].generalmemo,
              item.location.directions[directid].schedulingmemo,
              item.location.directions[directid].direction));
          });

          tmpSiteSlot.location = new SiteLocation(
            address, [item.location.coordinates[0], item.location.coordinates[1]],
            new Contact(item.location.contact.phoneNumber, item.location.contact.faxNumber),
            tempDirection,
            item.location.distance
          );

        });

      observer.next(tmpSiteSlot);
      observer.complete();

    });
  }

  getSites(
    startDate?: any,
    endDate?: any,
    latitude?: any,
    lognitude?: any,
    takeSiteSlotsize?: number,
    skipSiteSlotsize?: number,
    isBackToBackRequired: boolean = false,
    examSetWithTA?: Exam[],
    eligibilityId?: string,
    isGoogleMapsValid?: boolean
  ): Observable<any> {
    const startMonth = startDate.split('-')[0];
    const startDay = startDate.split('-')[1];
    const startYear = startDate.split('-')[2];
    const endMonth = endDate.split('-')[0];
    const endDay = endDate.split('-')[1];
    const endYear = endDate.split('-')[2];
    startDate = encodeURIComponent(moment([startYear, startMonth - 1, startDay]).format('YYYY-MM-DDTHH:mm:ss'));
    endDate = encodeURIComponent(moment([endYear, endMonth - 1, endDay]).format('YYYY-MM-DDTHH:mm:ss'));
    let baseSiteSlotUri: string = this.bootstrapParams.rootApiUrl + 'api/v1/sites/availabilities/?';
    let eligibilityIdUri: string = eligibilityId && eligibilityId !== '-1' ? '&eligibilityId=' + eligibilityId : '';
    let requestUri = baseSiteSlotUri + 'startDate=' + startDate +
      '&endDate=' + endDate +
      '&coordinates=' + latitude + ',' + lognitude +
      '&Take=' + takeSiteSlotsize + '&skip=' + skipSiteSlotsize
      + eligibilityIdUri + "&sss=true";

    if (!isGoogleMapsValid) {
      requestUri = baseSiteSlotUri + 'startDate=' + startDate +
        '&endDate=' + endDate +
        '&country=' + latitude + '&state=' + lognitude +
        '&Take=' + takeSiteSlotsize + '&skip=' + skipSiteSlotsize
        + eligibilityIdUri + "&sss=true";
    }

    return new Observable((observer: Observer<any>) => {
      this.httpService.search(
        requestUri, this.getApplicableTAList(examSetWithTA), this.bootstrapParams.Token, true
      )
        .pipe(map(this.getResultArray))
        .subscribe({
          next: (result) => {

            let siteSlots: SiteSlot[] = [];
            result = result.filter(r => r.type == "IBT" || r.availability.length);
            if (result.length == 0) {
              observer.next({
                siteSlots: siteSlots,
                error: ''
              });
              observer.complete();
            }

            result.forEach(slot => {
              // Fill Site Slot Stuffs
              let siteSlot: SiteSlot = new SiteSlot(null, null, null, null, null, null);
              siteSlot.id = slot.id;
              siteSlot.uri = slot.href;
              siteSlot.type = slot.type;
              siteSlot.prefix = slot.prefix;

              let addressLine: string[] = [];
              slot.location.address.addressLine.forEach((address: any) => {
                addressLine.push(address);
              });
              let address = new Address(
                addressLine,
                slot.location.address.adminDistrict,
                slot.location.address.country,
                slot.location.address.formattedAddress,
                slot.location.address.locality,
                slot.location.address.postalCode
              );

              let tempDirection: Direction[] = [];
              slot.location.directions.forEach((direction: any) => {
                tempDirection.push(
                  new Direction(direction.name,
                    direction.generalmemo,
                    direction.schedulingmemo,
                    direction.direction));
              });

              // Location
              siteSlot.location = new SiteLocation(address,
                slot.location.coordinates,
                new Contact(slot.location.contact.phoneNumber, slot.location.contact.faxNumber),
                tempDirection,
                slot.location.distance
              );

              // Site Availability
              siteSlot.availability = [];

              slot.availability.forEach((availability: any) => {
                siteSlot.availability.push(availability);
              });

              siteSlot.Testdate = siteSlot.type != "IBT" ? siteSlot.availability[0] : "";
              siteSlot.slots = [];
              siteSlots.push(siteSlot);
              if (result.length == siteSlots.length) {
                observer.next({
                  siteSlots: siteSlots,
                  error: ''
                });
                observer.complete();
              }
            });
          },
          error: (err: HttpErrorResponse) => {
            let errMessage = '';
            //Duration exceeds Battery MaxDuration
            if (err && err.status === 400) {
              const errorType = err.error[EligibilityErrorCodeMap.AppointmentTooLong];
              errMessage = errorType && errorType.length ? errorType[0]['Description'] : '';
            }
            let siteSlots: SiteSlot[] = [];
            observer.next({
              siteSlots: siteSlots,
              error: errMessage
            });
            observer.complete();
          }
        });
    });
  }

  findSites(
    examid: String,
    location1: any,
    location2: any,
    takeSiteSlotsize: number,
    skipSiteSlotsize: number,
    isMapSolutionValid: boolean
  ): Observable<SiteSlot[]> {
    let baseSiteSlotUri: string = this.bootstrapParams.rootApiUrl + 'api/v1/sites/?';
    let requestUri = baseSiteSlotUri + 'ExamId=' + examid + '&Take=' + takeSiteSlotsize + '&skip=' + skipSiteSlotsize;

    requestUri += (isMapSolutionValid ? '&coordinates=' + location1 + ',' + location2 : '&country=' + location1 + '&state=' + location2);

    return new Observable((observer: Observer<SiteSlot[]>) => {
      this.httpService.search(
        requestUri, null, this.bootstrapParams.Token
      )
        .pipe(map(this.getResultArray))
        .subscribe({
          next: (result) => {

            let siteSlots: SiteSlot[] = [];
            if (result.length == 0) {
              observer.next(siteSlots);
              observer.complete();
            }

            result.forEach(slot => {
              // Fill Site Slot Stuffs
              let siteSlot: SiteSlot = new SiteSlot(null, null, null, null, null, null);
              siteSlot.id = slot.id;
              siteSlot.uri = slot.href;
              siteSlot.type = slot.type;
              siteSlot.prefix = slot.prefix;


              let addressLine: string[] = [];
              slot.location.address.addressLine.forEach((addresLine: any) => {
                addressLine.push(addresLine);
              });
              let address = new Address(
                addressLine,
                slot.location.address.adminDistrict,
                slot.location.address.country,
                slot.location.address.formattedAddress,
                slot.location.address.locality,
                slot.location.address.postalCode
              );
              let tempDirection: Direction[] = [];

              slot.location.directions.forEach((direction: any) => {
                tempDirection.push(
                  new Direction(direction.name,
                    direction.generalmemo,
                    direction.schedulingmemo,
                    direction.direction));
              });

              // Location
              siteSlot.location = new SiteLocation(address,
                slot.location.coordinates,
                new Contact(slot.location.contact.phoneNumber, slot.location.contact.faxNumber),
                tempDirection,
                slot.location.distance
              );

              siteSlots.push(siteSlot);
              if (result.length == siteSlots.length) {
                observer.next(siteSlots);
                observer.complete();
              }
            });
          },
          error: (err) => {
            let siteSlots: SiteSlot[] = [];
            observer.next(siteSlots);
            observer.complete();
          }
        });
    });
  }

  getSlots(
    siteId?: string,
    StartDateTime?: any,
    endDateTime?: any,
    examSetWithTA?: Exam[],
    eligibilityId?: string,
    timeFormat?: boolean
  ): Observable<any> {
    let myslots: Slot[] = [];
    let baseSlotUri: string = this.bootstrapParams.rootApiUrl + 'api/v1/slots/' + '?siteId=' + siteId;
    let eligibilityIdUri: string = eligibilityId && eligibilityId !== '-1' ? '&eligibilityId=' + eligibilityId : '';


    const startMonth = StartDateTime.split('-')[0];
    const startDay = StartDateTime.split('-')[1];
    const startYear = StartDateTime.split('-')[2];
    const endMonth = endDateTime.split('-')[0];
    const endDay = endDateTime.split('-')[1];
    const endYear = endDateTime.split('-')[2];
    StartDateTime = encodeURIComponent(moment([startYear, startMonth - 1, startDay]).format('YYYY-MM-DDTHH:mm:ss'));
    endDateTime = encodeURIComponent(moment([endYear, endMonth - 1, endDay]).format('YYYY-MM-DDTHH:mm:ss'));

    return new Observable((observer: Observer<any>) => {
      this.httpService.search(baseSlotUri + eligibilityIdUri + '&startDate=' + StartDateTime + '&endDate=' + endDateTime, this.getApplicableTAList(examSetWithTA), this.bootstrapParams.Token, true)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (rNArray: any) => {
            let itinerary = (
              !!rNArray && !!rNArray.itinerary && !!rNArray.itinerary.length && !!rNArray.itinerary[0].tests ?
                rNArray.itinerary[0].tests :
                []
            );
            let subSlot: Slot = null;
            let resultSlot: any = rNArray.suggestions[0].slots;
            resultSlot.forEach((slot: any) => {
              subSlot = new Slot(
                slot.id,
                slot.uri,
                slot.startDateTime,
                moment(slot.startDateTime).format('YYYY-MM-DD'),
                this.utilityService.getTimeString(slot.startDateTime, timeFormat),
                this.utilityService.getStartEndFormat(
                  UtilityService.addMins(
                    slot.startDateTime,
                    slot.duration
                  )),
                slot.duration
              );
              myslots.push(subSlot);
            });
            observer.next({
              itinerary: itinerary,
              slots: myslots
            });
            observer.complete();
          },
          error: (err) => {
            observer.next({
              itinerary: [],
              slots: []
            });
            observer.complete();
          }
        });
    });

  }

  getSessions(siteId?: string, startDateTime?: any, endDateTime?: any, examSetWithTA?: Exam[], eligibilityId?: string, timeFormat?: boolean): Observable<Session[]> {
    let baseSlotUri: string = this.bootstrapParams.rootApiUrl + 'api/v1/slots/' + '?siteId=' + siteId;
    let eligibilityIdUri: string = eligibilityId && eligibilityId !== '-1' ? '&eligibilityId=' + eligibilityId : '';
    let sessions: Session[] = [];

    startDateTime = encodeURIComponent(startDateTime);
    endDateTime = encodeURIComponent(endDateTime);

    return new Observable((observer: Observer<Session[]>) => {
      this.httpService.search(baseSlotUri + eligibilityIdUri + '&startDate=' + startDateTime + '&endDate=' + endDateTime, this.getApplicableTAList(examSetWithTA), this.bootstrapParams.Token, true)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (rNArray: any) => {

            let resultSlot: any = rNArray.suggestions[0].slots;
            sessions = resultSlot.map((slot: any) => {
              let session: Session = new Session();
              session.slot = new Slot(
                slot.id,
                slot.uri,
                slot.startDateTime,
                moment(slot.startDateTime).format('YYYY-MM-DD'),
                this.utilityService.getTimeString(slot.startDateTime, timeFormat),
                this.utilityService.getStartEndFormat(
                  UtilityService.addMins(
                    slot.startDateTime,
                    slot.duration
                  )),
                slot.duration
              );
              session.groupId = slot.groupId;
              session.sessionId = slot.sessionId;
              return session;
            });

            observer.next(sessions);
            observer.complete();
          },
          error: (err) => {
            sessions = [];
            observer.next(sessions);
            observer.complete();
          }
        });
    });

  }

  getBookingDetails(bookingNumber: string, lastName: string): Observable<Booking> {
    let booking: Booking;
    return new Observable((observer: Observer<Booking>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/bookings/?confirmationNumber=' + bookingNumber + '&lastName=' + encodeURIComponent(lastName), this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (bookingResult: any) => {
            booking = new Booking();
            booking.id = bookingResult.id;
            booking.number = bookingNumber;
            booking.href = bookingResult.href;
            booking.tests = [];
            booking.payments = [];
            booking.hold = new Hold(bookingResult.tests[0].sessions[0].hold.id, bookingResult.tests[0].sessions[0].hold.href);

            //let candidateResult = this.getPersonalDetail('http://localhost:59321/api/v1/candidates/Hsrt3hSw50OEMuI9rrWQRg');
            let clientResult: any;
            let isClientLoaded: boolean = false;
            // Fill Payment Data
            booking.payments = bookingResult.payments;

            bookingResult.tests.forEach((test: any) => {
              let tempTest: Test = new Test(test.sessions, { id: test.eligibility.id, href: test.eligibility.href }, test.status);
              booking.testingAccommodations = [];
              tempTest.cnatt = test.cnatt;

              if (!!test.eligibility && !test.eligibility.id) {
                // Exam Set Call to check b2b for each exams for eligibility
                this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/exams/' + test.exam.id + '/set/', this.bootstrapParams.Token)
                  .pipe(map(this.getFirstOrDefault))
                  .subscribe({
                    next: (examSet: any) => {
                      this.selectedExamDetailService.examSetId = examSet.id;
                      if (examSet.attributes) {
                        if (!!examSet.attributes.b2b) {
                          this.selectedExamDetailService.isBackToBackRequired = true;
                          if (examSet.attributes.b2b.bufferTime > 0) {
                            this.selectedExamDetailService.setBatteryConfig(new BatteryConfig(examSet.attributes.b2b.bufferTime, examSet.attributes.b2b.maxDuration));
                          }
                        }
                        if (examSet.attributes.requiredSchedule) {
                          this.selectedExamDetailService.scheduleSetAttributeCode = true;
                        }
                        if (examSet.attributes.requiredReSchedule) {
                          this.selectedExamDetailService.rescheduleSetAttributeCode = true;
                        }
                        if (examSet.attributes.requiredCancel) {
                          this.selectedExamDetailService.cancelSetAttributeCode = true;
                        }
                      }
                    }
                  });
              }

              this.getObjectByURl(test.exam.href).subscribe((exam: any) => {
                if (exam != undefined && exam != null) {
                  tempTest.exam = Exam.convertAPIObjectToExam(exam);
                  // assign  specific TA to that exam
                  tempTest.exam.testingAccommodations = bookingResult.testingAccommodations;
                  let programHref = exam.program.href;
                  let programResult = this.getObjectByURl(programHref);

                  programResult.subscribe((program: any) => {
                    tempTest.program = { id: program.id, name: program.name, uri: program.uri, code: program.code };
                    if (!isClientLoaded) {
                      clientResult = this.getClientByURl(program.client.href + '?isPublished=true');
                      isClientLoaded = true;
                    }
                    booking.tests.push(tempTest);

                    if (bookingResult.tests.length == booking.tests.length) {
                      clientResult.subscribe((client: any) => {
                        booking.client = client;

                        if (program.isCandidateCentric) {
                          this.selectedExamDetailService.terminationUrl =
                            this.bootstrapParams.cmsUrl + 'login/' + client.code + '/' + program.code;
                        }

                        let prgResult = this.getObjectByURl(programHref + '?isPublished=true');
                        // if the program is not published
                        prgResult.subscribe((result) => {
                          if (result && result.status == "404") {
                            this.selectedExamDetailService.programNotPublished = true;
                          }

                          // TODO: can be done asynchronously only if c/p/e is available
                          let candidateResult = this.getPersonalDetail(bookingResult.candidate.id, client.code, program.code, exam.code);
                          candidateResult.subscribe((candidate) => {
                            booking.candidate = candidate
                            observer.next(booking);
                            observer.complete();
                          });
                        })

                      })
                    }
                  });
                }
              });

            });
          },
          error: (err) => {
            booking = new Booking();
            booking.id = "-1";
            observer.next(booking);
            observer.complete();
          }
        });
    });
  }

  getPersonalDetail(id: string, clientCode: string, programCode: string, examCode: string): Observable<Candidate> {
    return new Observable((observer: Observer<Candidate>) => {
      this.httpService.get(this.bootstrapParams.RegistrationRootApiUrl + 'api/v1/candidates/' + id +
        '?clientCode=' + clientCode + '&programCode=' + programCode + '&examCode=' + examCode, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (rArray) => {
            let candidateDetail: Candidate = new Candidate();
            candidateDetail.Id = rArray.id;
            candidateDetail.Href = rArray.href;
            candidateDetail.PrometricId = rArray.prometricId;
            candidateDetail.FirstName = rArray.firstName;
            candidateDetail.MiddleName = rArray.middleName;
            candidateDetail.LastName = rArray.lastName;
            candidateDetail.EmailAddress = rArray.emailAddress;
            candidateDetail.NewEmailAddress = rArray.newEmailAddress;
            candidateDetail.AddressLine1 = rArray.addressLine1;
            candidateDetail.AddressLine2 = rArray.addressLine2;
            candidateDetail.AddressLine3 = rArray.addressLine3;
            candidateDetail.AddressLine4 = rArray.addressLine4;
            candidateDetail.City = rArray.city;
            candidateDetail.Country = rArray.country;
            candidateDetail.PrimaryPhoneNumber = rArray.primaryPhoneNumber;

            candidateDetail.defaultDetails = new Dictionary();
            candidateDetail.defaultDetails.add('first_name', rArray.firstName);
            candidateDetail.defaultDetails.add('middle_name', rArray.middleName);
            candidateDetail.defaultDetails.add('last_name', rArray.lastName);
            candidateDetail.defaultDetails.add('email', rArray.emailAddress);
            candidateDetail.defaultDetails.add('address1', rArray.addressLine1);
            candidateDetail.defaultDetails.add('address2', rArray.addressLine2);
            candidateDetail.defaultDetails.add('address3', rArray.addressLine3);
            candidateDetail.defaultDetails.add('address4', rArray.addressLine4);
            candidateDetail.defaultDetails.add('h_phone', rArray.primaryPhoneNumber);
            candidateDetail.defaultDetails.add('h_phone_ext', rArray.primaryPhoneExtension);
            candidateDetail.defaultDetails.add('country', rArray.country);
            candidateDetail.defaultDetails.add('state', rArray.stateProvince);
            candidateDetail.defaultDetails.add('zipcode', rArray.postalCode);
            candidateDetail.defaultDetails.add('w_phone', rArray.secondaryPhoneNumber);
            candidateDetail.defaultDetails.add('w_phone_ext', rArray.secondaryPhoneExtension);
            candidateDetail.defaultDetails.add('f_phone', rArray.faxNumber);
            candidateDetail.defaultDetails.add('birth_date', rArray.birthDate);
            candidateDetail.defaultDetails.add('city', rArray.city);

            if (rArray.values && rArray.values.length) {
              rArray.values.forEach((demographic: any) => {
                candidateDetail.defaultDetails.add(demographic.name.toString().toLowerCase(), demographic.value);
              })
            }

            if (!!rArray.birthDate) {
              if (rArray.birthDate.split("T").length) {
                let dateStr = rArray.birthDate.split("T")[0].split("-")
                if (dateStr) {
                  let year = dateStr[0];
                  let month = dateStr[1];
                  let day = dateStr[2];

                  let birthDate = {
                    jsdate: new Date(year, month - 1, day)
                  }
                  candidateDetail.defaultDetails.add('birth_date', birthDate);
                }
              }
            }

            // no candidate country - skip country, state, county lookup to prevent errors
            if (!candidateDetail.defaultDetails["country"]) {
              observer.next(candidateDetail);
              observer.complete();
              return;
            }

            this.setCountryInfo(candidateDetail, candidateDetail.defaultDetails["country"].toLowerCase());
            observer.next(candidateDetail);
            observer.complete();
          }
        });
    });
  }

  // TODO: Make sure country code here is lowercase before comparing
  setCountryInfo(candidateDetail: Candidate, countryCode: string) {
    this.getCountries().subscribe(countries => {
      this.selectedExamDetailService.setCountries(countries);

      let country: Country[] = countries.filter(tempCountry => tempCountry.id.toLowerCase() === countryCode);

      if (country && country.length) {
        candidateDetail.defaultDetails['country'] = country[0].name;
        this.selectedExamDetailService.SetCandidateDataSource(candidateDetail);
      } else {
        // If the provided country is not in the list (i.e. end-dated countries such as PRK, SYR) it should not remain in the candidate demographics
        candidateDetail.defaultDetails.remove('country');
      }


      if (candidateDetail.defaultDetails["state"] || candidateDetail.defaultDetails["county_name"]) {
        ['state', 'county_name'].forEach(s => {
          // prepopulate state
          let stateCode = candidateDetail.defaultDetails[s] ? candidateDetail.defaultDetails[s].toString() : '';
          if (stateCode.length) {
            this.getProvinces(country[0].id).subscribe(
              regions => {
                if (stateCode.length < 3 && regions) {
                  let tempRegion = regions.filter(tempState => tempState.id.toLowerCase() == stateCode.toLowerCase());

                  if (tempRegion.length) {
                    candidateDetail.defaultDetails[s] = tempRegion[0].name;
                  }

                } else if (stateCode.length > 3 && regions) {
                  let tempRegion = regions.filter(tempState => tempState.name.toLowerCase() == stateCode.toLowerCase());

                  if (tempRegion.length) {
                    candidateDetail.defaultDetails[s] = tempRegion[0].name;
                  }

                }
                this.selectedExamDetailService.SetCandidateDataSource(candidateDetail);
              });
          }
        });
      }

    })
  }

  // Get testing Accomodation
  getTestingAccomodations(bookingId: string): Observable<any[]> {
    let taWithConfirmationNumber: any = [];
    return new Observable((observer: Observer<any>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/bookings/' + bookingId + '/testingaccommodations', this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((result: any) => {
          result.forEach((booking: any) => {
            this.getObjectByURl(booking.booking.href).subscribe((res: any) => {

              let tempTAWithConfimtaionNumber: any = { testingAccomodations: booking.testingAccommodations }
              taWithConfirmationNumber.push(tempTAWithConfimtaionNumber);
              if (result.length == taWithConfirmationNumber.length) {
                observer.next(taWithConfirmationNumber);
                observer.complete();
              }
            });
          });
        });
    });
  }

  getAppointmentDetails(appointmentId: string, allPayments?: boolean): Observable<Payment[]> {
    let returnPayments: Payment[] = [];
    return new Observable((observer: Observer<Payment[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/bookings/' + appointmentId + '/payments', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe({
          next: (rArray) => {
            rArray.forEach(item => {
              let payment: Payment;
              if (rArray.length == 0) {
                observer.next(returnPayments);
                observer.complete();
              } else {
                this.httpService.get(item.href, this.bootstrapParams.Token)
                  .pipe(map(this.getFirstOrDefault))
                  .subscribe({
                    next: (rNArray) => {
                      payment = new Payment(
                        rNArray.amount,
                        rNArray.currency,
                        rNArray.transactionType,
                        rNArray.accountNumber,
                        rNArray.confirmationNumber,
                        rNArray.description,
                        rNArray.examName,
                        rNArray.id,
                        rNArray.masterAppointmentId,
                        rNArray.masterPaymentId,
                        rNArray.status,
                        rNArray.code,
                        0,
                        rNArray.sequenceNumber,
                        0,
                        0,
                        rNArray.transactionDate,
                        rNArray.transactionId,
                        rNArray.settlementAmount,
                        rNArray.settlementCurrency
                      )
                      if (payment.transactionType == TransactionType.Schedule
                        || payment.transactionType == TransactionType.Reschedule
                        || payment.transactionType == TransactionType.Cancel) {
                        returnPayments.push(payment);
                      }

                      // todo: remove this from here - MM
                      if (payment.transactionType == TargetFlow.Schedule) {
                        this.selectedExamDetailService.scheduleTransactionId = payment.transactionId;
                      }

                      if (returnPayments.length == rArray.length) {
                        observer.next(returnPayments);
                        observer.complete();
                      }
                    }
                  });
              }
            });
          },
          error: (err) => {
            let returnPayments: Payment[] = [];
            observer.next(returnPayments);
            observer.complete();
          }
        });
    });
  }

  getAppointmentSlot(url: string): Observable<AppointmentSlot> {
    return new Observable((observer: Observer<AppointmentSlot>) => {
      this.httpService.get(url, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((holdResponse) => {

          this.httpService.get(holdResponse.slot.href, this.bootstrapParams.Token)
            .pipe(map(this.getFirstOrDefault))
            .subscribe((slotResponse) => {
              let appointmentSlot: AppointmentSlot = new AppointmentSlot(
                slotResponse.id,
                slotResponse.uri,
                slotResponse.startDateTime,
                slotResponse.duration,
                null,
                this.utilityService.getTimeString(slotResponse.startDateTime, this.selectedExamDetailService.timeFormat),
                this.utilityService.getStartEndFormat(
                  UtilityService.addMins(
                    slotResponse.startDateTime,
                    slotResponse.duration
                  )
                )
              );

              // the site id in slot object is the id to be used for vouchers to work
              var siteId = slotResponse.site.id;
              this.httpService.get(slotResponse.site.href, this.bootstrapParams.Token)
                .pipe(map(this.getFirstOrDefault))
                .subscribe((rArray) => {
                  let site: any = rArray;
                  appointmentSlot.site = site;
                  appointmentSlot.site.id = siteId;
                  // this is to override the site timezone info and startdatetime in all flows
                  if (!!holdResponse.proctorTimeZone) {
                    appointmentSlot.site.location.directions[0].name = holdResponse.proctorTimeZone + ' Proctoring Site';
                    appointmentSlot.site.location.timeZone = holdResponse.proctorTimeZone + ' Proctoring Site';
                    appointmentSlot.startDateTime = holdResponse.proctorStart;
                    this.getTimezones(1).subscribe((timezones: Timezone[]) => {
                      const tz = timezones.filter((t: Timezone) => { return t.code.toLowerCase() === holdResponse.proctorTimeZone.toLowerCase()})
                      if (tz && tz.length) {
                        appointmentSlot.proctorTimeZone = tz[0];
                      }
                    });
                  }
                  observer.next(appointmentSlot);
                  observer.complete();
                });
            });

        });
    });
  }

  getPayment(paymentItem: any): Observable<Payment> {
    let payment: Payment;
    return new Observable((observer: Observer<Payment>) => {
      this.httpService.get(paymentItem.href, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((rNArray) => {
          payment = new Payment(
            rNArray.amount,
            rNArray.currency,
            rNArray.transactionType,
            rNArray.accountNumber,
            rNArray.confirmationNumber,
            rNArray.description,
            rNArray.examName,
            null,
            rNArray.masterAppointmentId,
            null,
            rNArray.paymentStatus,
            rNArray.code,
            null,
            rNArray.sequenceNumber,
            0, 0,
            rNArray.transactionDate
          );
        });
    });
  }

  holdSiteSlot(holdSlot: Hold): Observable<number> {
    return new Observable((observer: Observer<any>) => {
      let holdRequestData: any =
      {
        "slots": [{
          "id": holdSlot.slot.id
        }]
      };
      this.httpService.post(this.bootstrapParams.rootApiUrl + 'api/v2/holds', holdRequestData, this.bootstrapParams.Token, true)
        .pipe(map(this.getRawResult))
        .subscribe({
          next: (rNArray) => {
            if (rNArray != null && rNArray != undefined && rNArray.length > 0) {
              observer.next(rNArray[0].id);
              observer.complete();
            }
            else {
              observer.next(-2);
              observer.complete();
            }
          },
          error: (err) => {
            if (err && err.status == 409) {
              observer.next(-2);
              observer.complete();
            }
            else {
              observer.next(-1);
              observer.complete();
            }
          }
        });
    });
  }

  holdMultipleSlots(slots: Slot[]): Observable<any[]> {
    return new Observable((observer: Observer<any>) => {
      let holdRequestData;
      holdRequestData = {
        "slots": slots.map((s) => {
          return { "id": s.id };
        })
      }

      this.httpService.post(this.bootstrapParams.rootApiUrl + 'api/v2/holds', holdRequestData, this.bootstrapParams.Token, true)
        .pipe(map(this.getRawResult))
        .subscribe({
          next: (rNArray) => {
            if (rNArray != null && rNArray != undefined && rNArray.length > 0) {
              observer.next(rNArray);
              observer.complete();
            } else {
              observer.next(-2);
              observer.complete();
            }
          },
          error: (err) => {
            observer.next(-1);
            observer.complete();
          }
        });
    });
  }

  holdProctor(holdSlot: Hold, timezone: string, examsetWithTA: Exam[]): Observable<Hold[]> {
    return new Observable((observer: Observer<any>) => {
      let holdRequestData: any =
      {
        "slot": {
          "id": holdSlot.slot.id
        },
        "timezone": timezone,
        "exams": this.getApplicableTAList(examsetWithTA)
      };
      this.httpService.post(this.bootstrapParams.rootApiUrl + 'api/v2/holds/holdProctor', holdRequestData, this.bootstrapParams.Token)
        .pipe(map(this.getRawResult))
        .subscribe({
          next: (rNArray) => {
            if (rNArray != null && rNArray != undefined && rNArray.length > 0) {
              let holds: Hold[] = rNArray.map(rItem => {
                return Hold.convertAPIObjectToHold(rItem);
              });
              observer.next(holds);
              observer.complete();
            }
          },
          error: (err) => {
            if (err && err.status == 409) {
              observer.next(-2);
              observer.complete();
            }
            else {
              observer.next(-1);
              observer.complete();
            }
          }
        });
    });
  }

  releaseHold(holdSlot: Hold): Observable<number> {
    return new Observable((observer: Observer<any>) => {

      this.httpService.delete(this.bootstrapParams.rootApiUrl + 'api/v1/holds/' + holdSlot.id, this.bootstrapParams.Token)
        .pipe(map(this.getRawResult))
        .subscribe({
          next: (rNArray) => {
            observer.next(true);
            observer.complete();
          },
          error: (err) => {
            observer.next(-1);
            observer.complete();
          }
        });
    });
  }

  getSlotCountry(slotId: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {

      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/slots/' + slotId, this.bootstrapParams.Token, null, null, null, null, true)
        .pipe(map(this.getRawResult))
        .subscribe({
          next: (response) => {

            if (response && response["site"]) {

              this.httpService.get(response["site"].href, this.bootstrapParams.Token)
                .pipe(map(this.getFirstOrDefault))
                .subscribe({
                  next: (item) => {

                    let tmpSiteSlot = new SiteSlot(null, null, null, null, null, null);

                    //tmpSiteSlot.id = item.id;
                    tmpSiteSlot.uri = item.uri;
                    tmpSiteSlot.type = item.type;
                    tmpSiteSlot.prefix = item.prefix;
                    tmpSiteSlot.id = response["site"]["id"];

                    let address = new Address(
                      item.location.address.addressLine,
                      item.location.address.adminDistrict,
                      item.location.address.country,
                      item.location.address.formattedAddress,
                      item.location.address.locality,
                      item.location.address.postalCode
                    );

                    let tempDirection: Direction[] = [];
                    for (let directid in item.location.directions) {
                      tempDirection.push(
                        new Direction(item.location.directions[directid].name,
                          item.location.directions[directid].generalmemo,
                          item.location.directions[directid].schedulingmemo,
                          item.location.directions[directid].direction)
                      );
                    }

                    tmpSiteSlot.location = new SiteLocation(address,
                      item.location.coordinates,
                      new Contact(item.location.contact.phoneNumber, item.location.contact.faxNumber),
                      tempDirection,
                      item.location.distance
                    );
                    observer.next(tmpSiteSlot);
                    observer.complete();

                  }
                });
            }
          },
          error: (err) => {
            observer.next(-1);
            observer.complete();
          }
        });
    });
  }

  validateHold(holdId: any): Observable<boolean> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/holds/' + holdId + '?validate=true', this.bootstrapParams.Token)
        .pipe(map(this.getRawResult))
        .subscribe({
          next: (rNArray) => {
            observer.next(true);
            observer.complete();
          },
          error: (err) => {
            observer.next(false);
            observer.complete();
          }
        });
    })
  }

  extendHold(holdId: any): Observable<boolean> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.put(this.bootstrapParams.rootApiUrl + 'api/v1/holds/' + holdId, { minutes: this.bootstrapParams.extendHoldTime }, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (response) => {
            if (response != null && response != undefined) {
              observer.next(true);
              observer.complete();
            } else {
              observer.next(false);
              observer.complete();
            }
          },
          error: (err) => {
            observer.error(err);
            observer.complete();
          }
        });
    });
  }

  prepareBookingUpdate(
    paymentDetails: CreatePaymentSessionRequest,
    bookingUpdateRequestData: any
  ): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      let msgResp = { message: "errors.thereWasAnIssue", status: false, confirmationNumber: "Error", response: "", errorId: SchedulingErrors.Default };
      let culture = LanguageCultureCodes.forEach((lang) => {
        if (lang.key === this.selectedExamDetailService.selectedLanguageCode) return lang.culture;
      });

      bookingUpdateRequestData['culture'] = culture;

      if (!!bookingUpdateRequestData['tests'][0] && !!bookingUpdateRequestData['tests'][0]['sessions'][0].hold.id) {
        let validatedHolds$: Observable<boolean>[] = [];

        bookingUpdateRequestData['tests'][0]['sessions'].forEach((session: any) => {
          validatedHolds$.push(this.validateHold(session.hold.id));
        });

        forkJoin(validatedHolds$)
          .pipe(mergeMap((response: boolean[]) => {
            if (!!response && !!response.length && response.every(v => !!v)) {
              return this.paymentService.createSession(paymentDetails);
            } else {
              const holdId = bookingUpdateRequestData['tests'][0]["sessions"][0].hold.id;
              this.metricApiService.log(LogLevel.Error, `Validate hold call returned expired when checking for hold ${holdId}`);
              this.timerService.expireTimer();
            }
          }),
          catchError((err) => {
            if (err.message === 'Orchestrator unreachable') {
              return throwError(() => new Error('Payment Orchestrator Communitcation Error'));
            }
            return throwError(() => new Error('Validate Hold Error'));
          }),
          mergeMap((orchestratorResponse: CreatePaymentSessionResponse) => {
            if (!!orchestratorResponse && !!orchestratorResponse.paymentOrchestratorId) {
              this.selectedExamDetailService.setPaymentOrchestratorSessionDetail(orchestratorResponse);

              let extendedHolds$: Observable<any>[] = [];

              bookingUpdateRequestData['tests'][0]['sessions'].forEach((session: any) => {
                extendedHolds$.push(this.extendHold(session.hold.id));
              });

              return forkJoin(extendedHolds$);
            } else {
              return throwError(() => new Error('Payment Orchestrator Error'));
            }
          }),
          catchError((err) => {
            if (err.message === 'Payment Orchestrator Communitcation Error' || err.message === 'Validate Hold Error') {
              return throwError(() => err);
            }
            return throwError(() => new Error('Payment Orchestrator Error'));
          }))
          .subscribe({
            next: (extendResponse) => {
              if (!extendResponse || !extendResponse.length) {
                return throwError(() => new Error('Hold Extension Error'));
              }

              this.selectedExamDetailService.setBookingRequestData(bookingUpdateRequestData);

              msgResp = {
                message: "",
                status: true,
                confirmationNumber: "",
                response: "",
                errorId: ""
              };
              observer.next(msgResp);
              observer.complete();
            },
            error: (err) => {
              if (err.message === 'Validate Hold Error') {
                msgResp = {
                  message: "errors.thereWasAnIssue",
                  status: false,
                  confirmationNumber: "Error",
                  response: "",
                  errorId: SchedulingErrors.HoldError
                };
                observer.next(msgResp);
              } else if (err.message === 'Payment Orchestrator Communitcation Error') {
                msgResp = {
                  message: "errors.thereWasAnIssue",
                  status: false,
                  confirmationNumber: "Error",
                  response: "",
                  errorId: SchedulingErrors.OrchestratorCommunicationError
                };
                observer.next(msgResp);
              } else if (err.message === 'Payment Orchestrator Error') {
                msgResp = {
                  message: "errors.thereWasAnIssue",
                  status: false,
                  confirmationNumber: "Error",
                  response: "",
                  errorId: SchedulingErrors.OrchestratorError
                };
                observer.next(msgResp);
              } else if (err.message === 'Hold Extension Error') {
                msgResp = {
                  message: "errors.thereWasAnIssue",
                  status: false,
                  confirmationNumber: "Error",
                  response: "",
                  errorId: SchedulingErrors.HoldError
                };
                observer.next(msgResp);
              } else {
                msgResp = {
                  message: "errors.thereWasAnIssue",
                  status: false,
                  confirmationNumber: "Error",
                  response: "",
                  errorId: SchedulingErrors.Default
                };
                observer.next(msgResp);
              }

              observer.complete();
            }
          });
      }
    });
  }

  completeBookingUpdate(
    reScheduleBookingRequestData: any,
    paymentSessionDetails: CreatePaymentSessionResponse,
    examDetails: any[],
    metricDetails?: MetricDetails[]
  ): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.paymentSessionData = null;

      let msgResp = {
        message: "errors.thereWasAnIssue",
        status: false,
        confirmationNumber: "Error",
        response: "",
        errorId: SchedulingErrors.Default
      };

      const hold = reScheduleBookingRequestData['tests'][0]['sessions'][0].hold;

      // validate hold
      let validatedHolds$: Observable<boolean>[] = [];

      reScheduleBookingRequestData['tests'][0]['sessions'].forEach((session: any) => {
        validatedHolds$.push(this.validateHold(session.hold.id));
      });

      forkJoin(validatedHolds$)
        .pipe(mergeMap((validateResponse: boolean[]) => {
          if (!!validateResponse && !!validateResponse.length && validateResponse.every(v => !!v)) {
            // verify payment
            return this.paymentService.verifyPayment(paymentSessionDetails.paymentOrchestratorId, hold.id)
          } else {
            return throwError(() => new Error('Validate Hold Error'));
          }
        }),
        catchError((err) => {
          return throwError(() => new Error('Validate Hold Error'));
        }),
        mergeMap((verifyResponse) => {
          if (!!verifyResponse) {
            this.paymentSessionData = verifyResponse;
            this.paymentSessionData['accountId'] = paymentSessionDetails.accountId;

            // stage payment
            return this.paymentService.stageSessionPayment(this.paymentSessionData, CardFee.Reschedule, examDetails, hold.id);
          }

          return throwError(() => new Error('Payment Verification Error'));
        }),
        catchError((err) => {
          if (err.message === 'Validate Hold Error') {
            return throwError(() => err);
          }
          return throwError(() => new Error('Payment Verification Error'));
        }),
        mergeMap((stageResponse) => {
          if (!!stageResponse) {
            reScheduleBookingRequestData.payments.push(this.paymentSessionData.paymentOrchestratorId);

            // Update booking
            return this.updateBooking(reScheduleBookingRequestData);
          }

          return throwError(() => new Error('Stage Payment Error'));
        }),
        catchError((err) => {
          if (err.message === 'Payment Verification Error' || err.message === 'Stage Payment Error' || err.message === 'Validate Hold Error') {
            return throwError(() => err);
          }
          return throwError(() => new Error('Booking Error'));
        }))
        .subscribe({
          next: (bookingResponse) => {
            if (bookingResponse != undefined && bookingResponse != null) {
              if (bookingResponse.tests.length > 0) {
                if (bookingResponse.tests[0].sessions[0] != null && bookingResponse.tests[0].sessions[0] != undefined) {
                  msgResp = { message: "reconfirmationComponent.appointmentConfirmationMessage", status: true, confirmationNumber: bookingResponse.tests[0].sessions[0].confirmationNumber, response: bookingResponse, errorId: "" };
                }
                if (!msgResp.status) {
                  msgResp = {
                    message: "errors.thereWasAnIssueRefund",
                    status: false,
                    confirmationNumber: "Error",
                    response: "",
                    errorId: SchedulingErrors.BookingError
                  };
                }
                else {
                  // checking for length of metrics as no metrics are collected without google maps
                  if (metricDetails && metricDetails.length) {
                    this.metricApiService.postMetricDetails(metricDetails[0], reScheduleBookingRequestData.id, "ReSchedule");
                  }

                  if (this.selectedExamDetailService.isBiometricConsentCheckedReschedule) {
                    this.postBiometrics(bookingResponse.tests).subscribe(() => { });
                  }

                }

                observer.next(msgResp);
                observer.complete();
              }
            }
          },
          error: (err) => {
            if (err.message === 'Validate Hold Error') {
              msgResp = {
                message: "errors.thereWasAnIssue",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.HoldError
              };
              observer.next(msgResp);
            } else if (err.message === 'Payment Verification Error') {
              msgResp = {
                message: "errors.thereWasAnIssue",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.OrchestratorError
              };
            } else if (err.message === 'Stage Payment Error') {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.StagingError
              };
            } else if (err.message === 'Booking Error') {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.BookingError
              };
            } else {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.Default
              };
            }

            this.metricApiService.log(LogLevel.Error, `Complete reschedule ${err} call failure` + `\nHold: ${hold.id}`);

            if (!!this.paymentSessionData && err !== 'Payment Verification Error') {
              this.metricApiService.log(LogLevel.Debug, `Rolling back payment transaction due to ${err}\nHold: ${hold.id}`);
              this.paymentService.rollBackSessionTransaction(this.paymentSessionData.paymentOrchestratorId, this.paymentSessionData.cost).subscribe({
                next: (resp: any) => {
                  if (resp != undefined && resp != null) {
                    if (!resp.paymentOrchestratorRefundId) {
                      this.metricApiService.log(LogLevel.Error, `Issue rolling back payment.\nHold: ${hold.id}\nPaymentOrchestratorId: ${this.paymentSessionData.paymentOrchestratorId}`);
                      msgResp = {
                        message: "errors.thereWasAnIssueRefund",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.RollbackError
                      };
                    }
                  }
                },
                error: (err) => {
                  if (err === 'Transaction Used') {
                    observer.next(msgResp);
                  } else if (err === 'Orchestrator unreachable') {
                    this.metricApiService.log(LogLevel.Error, `Unable to roll back payment due to 429.\nHold: ${hold.id}\nPaymentOrchestratorId: ${this.paymentSessionData.paymentOrchestratorId}`);
                    msgResp = {
                      message: "errors.thereWasAnIssueRefund",
                      status: false,
                      confirmationNumber: "Error",
                      response: "",
                      errorId: SchedulingErrors.OrchestratorCommunicationError
                    };
                  } else {
                    this.metricApiService.log(LogLevel.Error, `Issue rolling back payment.\nHold: ${hold.id}\nPaymentOrchestratorId: ${this.paymentSessionData.paymentOrchestratorId}`);
                    msgResp = {
                      message: "errors.thereWasAnIssueRefund",
                      status: false,
                      confirmationNumber: "Error",
                      response: "",
                      errorId: SchedulingErrors.RollbackError
                    };
                    observer.next(msgResp);
                  }
                }
              });
            }

            if (err !== 'Validate Hold Error') {
              reScheduleBookingRequestData['tests'][0]['sessions'].forEach((session: any) => {
                this.releaseHold(session.hold).subscribe(() => { });
              });
            }

            observer.next(msgResp);
            observer.complete();
          }
        });
    });
  }

  reScheduleBooking(
    reScheduleBookingRequestData: any,
    cardDetail?: any,
    appointmentId?: any,
    examDetailsForSettlement?: any,
    metricDetails?: MetricDetails[],
    isBackToBackRequired?: boolean
  ): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      let msgResp = { message: "reconfirmationComponent.tryLater", status: false, confirmationNumber: "Error", response: "" };
      let culture = LanguageCultureCodes.forEach((lang) => {
        if (lang.key === this.selectedExamDetailService.selectedLanguageCode) return lang.culture;
      });

      reScheduleBookingRequestData['culture'] = culture;
      reScheduleBookingRequestData['sendConfirmation'] = true; // set a true value to sendEmail

      // TODO: Convert this into FlatMap in order to create just one observable.
      if (reScheduleBookingRequestData['tests'][0] && reScheduleBookingRequestData['tests'][0]["sessions"][0].hold.id) {
        this.validateHold(reScheduleBookingRequestData['tests'][0]["sessions"][0].hold.id).subscribe(response => {
          if (response) {
            this.paymentService.validateCardDetails(cardDetail).subscribe((response: any) => {
              if (response.isAuthorized == true) {
                reScheduleBookingRequestData.payments.push(response.transactionId);
                this.httpService.put(this.bootstrapParams.rootApiUrl + 'api/v1/bookings', reScheduleBookingRequestData, this.bootstrapParams.Token)
                  .pipe(map(this.getFirstOrDefault))
                  .subscribe({
                    next: (rNArray) => {
                      if (rNArray != undefined && rNArray != null) {
                        if (rNArray.tests.length > 0) {
                          if (rNArray.tests[0].sessions[0] != null && rNArray.tests[0].sessions[0] != undefined) {
                            msgResp = { message: "reconfirmationComponent.appointmentConfirmationMessage", status: true, confirmationNumber: rNArray.tests[0].sessions[0].confirmationNumber, response: rNArray };
                          }
                        }
                        if (!msgResp.status) {
                          this.paymentService.rollBackTransaction(response.transactionId).subscribe((resp: any) => {
                            if (resp != undefined && resp != null) {
                              if (!resp.isCancelled) {
                                msgResp = { message: "reconfirmationComponent.tryLater", status: false, confirmationNumber: "Error", response: "" };
                              }
                              observer.next(msgResp);
                              observer.complete();
                            }
                          });
                        }
                        else {
                          // checking for length of metrics as no metrics are collected without google maps
                          if (metricDetails && metricDetails.length) {
                            this.metricApiService.postMetricDetails(metricDetails[0], reScheduleBookingRequestData.id, "ReSchedule");
                          }

                          if (this.selectedExamDetailService.isBiometricConsentCheckedReschedule) {
                            this.postBiometrics(rNArray.tests).subscribe(() => { });
                          }

                        }

                        observer.next(msgResp);
                        observer.complete();
                      }
                    },
                    error: (err) => {
                      //Need To Implement Global Error Handling (Remove Code Below)
                      let resp = { message: "schedulePaymentComponent.errorMessage", status: false, confirmationNumber: "Error" };
                      this.paymentService.rollBackTransaction(response.transactionId).subscribe((resp: any) => {
                        if (resp != undefined && resp != null) {
                          //if (!resp.isCancelled) {
                          resp = { message: "schedulePaymentComponent.errorMessage", status: false, confirmationNumber: "Error" };
                          //}
                          observer.next(resp);
                          observer.complete();
                        }
                      });
                    }
                  });
              }
              else {
                msgResp = { message: "schedulePaymentComponent.paymentErrorMessage", status: false, confirmationNumber: "", response: "" };
                observer.next(msgResp);
                observer.complete();
              }
            });
          } else {
            const holdId = reScheduleBookingRequestData['tests'][0]["sessions"][0].hold.id;
            this.metricApiService.log(LogLevel.Error, `Validate hold call returned expired when checking for hold ${holdId}`);
            this.timerService.expireTimer();
          }
        });
      }
    });
  }

  holdExamsBackToBackRequired(holdExamsData: any): Observable<Hold[]> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.post(this.bootstrapParams.rootApiUrl + 'api/v2/holds', holdExamsData, this.bootstrapParams.Token, true)
        .pipe(map(this.getRawResult))
        .subscribe({
          next: (rNArray) => {
            let holds: Hold[] = rNArray.map(rItem => {
              return Hold.convertAPIObjectToHold(rItem);
            });

            if (holds != null && holds != undefined && holds.length > 0) {
              observer.next(holds);
              observer.complete();
            }
            else {
              observer.next(-2);
              observer.complete();
            }
          },
          error: (err) => {
            if (err && err.status == 409) {
              observer.next(-2);
              observer.complete();
            }
            else {
              observer.next(-1);
              observer.complete();
            }
          }
        });
    });
  }

  prepareBookingCancel(
    paymentDetails: CreatePaymentSessionRequest
  ): Observable<any> {
    let msgResp = { message: "errors.thereWasAnIssue", status: false, confirmationNumber: "Error", response: "", errorId: SchedulingErrors.Default };

    return new Observable((observer: Observer<any>) => {
      this.paymentService.createSession(paymentDetails).subscribe({
        next: (response: CreatePaymentSessionResponse) => {
          if (!!response && !!response.paymentOrchestratorId) {
            this.selectedExamDetailService.setPaymentOrchestratorSessionDetail(response);

            msgResp = { message: "", status: true, confirmationNumber: "", response: "", errorId: "" };
          } else {
            msgResp = {
              message: "errors.thereWasAnIssue",
              status: false,
              confirmationNumber: "Error",
              response: "",
              errorId: SchedulingErrors.OrchestratorError
            };
          }
          observer.next(msgResp);
          observer.complete();
        },
        error: (err) => {
          if (err === 'Orchestrator unreachable') {
            msgResp = {
              message: "errors.thereWasAnIssue",
              status: false,
              confirmationNumber: "Error",
              response: "",
              errorId: SchedulingErrors.OrchestratorCommunicationError
            };
          }
          observer.next(msgResp);
          observer.complete();
        }
      });
    });
  }

  completeBookingCancel(
    appointmentIds: string[],
    examAccommodationsForNativeDelete: any[],
    paymentSessionDetails: CreatePaymentSessionResponse,
    examDetails: any[],
    voucherPayment?: Payment[],
    confirmationNumber?: string
  ): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      let msgResp = {
        message: "errors.thereWasAnIssue",
        status: false,
        confirmationNumber: "Error",
        response: "",
        errorId: SchedulingErrors.Default
      };

      this.paymentService.verifyPayment(paymentSessionDetails.paymentOrchestratorId, null)
        .pipe(mergeMap((verifyResponse: PaymentSession) => {
          if (!!verifyResponse) {
            this.paymentSessionData = verifyResponse;
            this.paymentSessionData['accountId'] = paymentSessionDetails.accountId;

            // stage payment
            return this.paymentService.stageSessionPayment(this.paymentSessionData, CardFee.Cancel, examDetails, null);
          }

          return throwError(() => new Error('Payment Verification Error'));
        }),
        catchError((err) => {
          return throwError(() => new Error('Payment Verification Error'));
        }),
        mergeMap((stageResponse) => {
          if (!!stageResponse) {

            // Update booking
            return this.deleteBooking(appointmentIds, examAccommodationsForNativeDelete, this.paymentSessionData.paymentOrchestratorId);
          }

          return throwError(() => new Error('Stage Payment Error'));
        }),
        catchError((err) => {
          if (err.message === 'Payment Verification Error') {
            return throwError(() => err);
          }
          return throwError(() => new Error('Stage Payment Error'));
        }))
        .subscribe({
          next: (deleteResponse: any) => {
            if (!!deleteResponse && deleteResponse.status) {
              if (voucherPayment && voucherPayment.length) {
                this.unredeemVoucher(voucherPayment, confirmationNumber).subscribe(() => { })
              }
              observer.next(deleteResponse);
              observer.complete();
            } else {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.BookingError
              };
              observer.next(msgResp);
              observer.complete();
            }
          },
          error: (err) => {
            if (err.message === 'Payment Verification Error') {
              msgResp = {
                message: "errors.thereWasAnIssue",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.OrchestratorError
              };
            } else if (err.message === 'Stage Payment Error') {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.StagingError
              };
            } else if (err.message === 'Booking Error') {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.BookingError
              };
            } else {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.Default
              };
            }

            this.metricApiService.log(LogLevel.Error, `Complete cancel ${err} call failure`);

            if (!!this.paymentSessionData && err.message !== 'Payment Verification Error') {
              this.metricApiService.log(LogLevel.Debug, `Rolling back payment transaction due to ${err}`);
              this.paymentService.rollBackSessionTransaction(this.paymentSessionData.paymentOrchestratorId, this.paymentSessionData.cost).subscribe({
                next: (resp: any) => {
                  if (resp != undefined && resp != null) {
                    if (!resp.paymentOrchestratorRefundId) {
                      this.metricApiService.log(LogLevel.Error, `Issue rolling back payment.\nPaymentOrchestratorId: ${this.paymentSessionData.paymentOrchestratorId}`);
                      msgResp = {
                        message: "errors.thereWasAnIssueRefund",
                        status: false,
                        confirmationNumber: "Error",
                        response: "",
                        errorId: SchedulingErrors.RollbackError
                      };
                    }
                  }
                },
                error: (err) => {
                  if (err.message === 'Transaction Used') {
                    observer.next(msgResp);
                  } else if (err.message === 'Orchestrator unreachable') {
                    this.metricApiService.log(LogLevel.Error, `Unable to roll back payment due to 429.\nPaymentOrchestratorId: ${this.paymentSessionData.paymentOrchestratorId}`);
                    msgResp = {
                      message: "errors.thereWasAnIssueRefund",
                      status: false,
                      confirmationNumber: "Error",
                      response: "",
                      errorId: SchedulingErrors.OrchestratorCommunicationError
                    };
                  } else {
                    this.metricApiService.log(LogLevel.Error, `Issue rolling back payment.\nPaymentOrchestratorId: ${this.paymentSessionData.paymentOrchestratorId}`);
                    msgResp = {
                      message: "errors.thereWasAnIssueRefund",
                      status: false,
                      confirmationNumber: "Error",
                      response: "",
                      errorId: SchedulingErrors.RollbackError
                    };
                    observer.next(msgResp);
                  }
                }
              });
            }

            observer.next(msgResp);
            observer.complete();
          }
        });
    });
  }

  cancelBooking(
    appointmentIds: string[],
    examAccommodationsForNativeDelete: any[],
    cardDetail?: any,
    voucherPayment?: Payment[],
    confirmationNumber?: string
  ): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      let msgResp = { message: "cancellationConfirmationComponent.tryLater", status: false, confirmationNumber: "Error", response: "" };

      this.paymentService.validateCardDetails(cardDetail).subscribe((cardResponse: any) => {
        if (cardResponse.isAuthorized == true) {

          this.deleteBooking(appointmentIds, examAccommodationsForNativeDelete, cardResponse.transactionId).subscribe({
            next: (response: any) => {
              if (!!response && response.status) {
                if (voucherPayment && voucherPayment.length) {
                  this.unredeemVoucher(voucherPayment, confirmationNumber).subscribe(() => { })
                }
                observer.next(response);
                observer.complete();
              } else {
                this.paymentService.rollBackTransaction(cardResponse.transactionId).subscribe({
                  next: (resp: any) => {
                    if (resp != undefined && resp != null) {
                      if (!resp.isCancelled) {
                        msgResp = { message: "cancellationConfirmationComponent.tryLater", status: false, confirmationNumber: "Error", response: "" };
                      }
                      observer.next(msgResp);
                      observer.complete();
                    }
                  },
                  error: (err) => {
                    observer.next(msgResp);
                    observer.complete();
                  }
                });
              }
            }
          });
        }
        else {
          msgResp = { message: "schedulePaymentComponent.paymentErrorMessage", status: false, confirmationNumber: "", response: "" };
          observer.next(msgResp);
          observer.complete();
        }
      })

    });
  }

  // Remove this method once Travserse logic is tested.
  getExamByURl(url: string): Observable<Exam> {
    return new Observable((observer: Observer<Exam>) => {
      this.httpService.get(url, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((rObject) => {
          observer.next(Exam.convertAPIObjectToExam(rObject));
          observer.complete();
        });
    });
  }

  // Remove this method once Travserse logic is tested.
  getProgramByURl(url: string): Observable<Program> {
    return new Observable((observer: Observer<Program>) => {
      this.httpService.get(url, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((rObject) => {
          let program: Program = new Program(
            rObject.id,
            rObject.code,
            rObject.client.href,
            rObject.name
          );

          observer.next(program);
          observer.complete();
        });
    });
  }

  // Remove this method once Travserse logic is tested.
  getClientByURl(url: string): Observable<Client> {
    return new Observable((observer: Observer<Client>) => {
      this.httpService.get(url, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((rObject) => {
          let client: Client = new Client(rObject.id, rObject.code, rObject.uri, rObject.name, rObject.sso, false);

          observer.next(client);
          observer.complete();
        });
    });
  }

  getObjectByURl(url: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.get(url, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (rObject) => {
            observer.next(rObject);
            observer.complete();
          },
          error: (error) => observer.next(error)
        });
    });
  }

  private getResultArray(apiResponse: any): any[] {
    let resultArray = apiResponse.results;
    return resultArray || [];
  }

  private getSingleResult(apiResponse: any): any[] {
    let resultArray = apiResponse.results;
    return resultArray.slots;
  }

  private getRawResult(apiResponse: any): any[] {
    return apiResponse;
  }

  private getFirstOrDefault(apiResponse: any): any {
    let resultArray: any = (apiResponse.results !== undefined) ? apiResponse.results : apiResponse;
    return resultArray;
  }

  /**
  * Function to get Client info using Client id
  * @param code
  */
  getClient(code: string): Observable<Client[]> {
    return new Observable((observer: Observer<Client[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/Clients/?code=' + code + '&isPublished=true', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe((rArray) => {
          let client: Client[] = rArray.map(rItem => {
            return {
              id: rItem.id,
              name: rItem.name,
              uri: rItem.uri,
              code: rItem.code,
              sso: rItem.sso,
              requiresBiometricConsent: rItem.requiresBiometricConsent,
              location: rItem.location
            }
          });
          observer.next(client);
          observer.complete();
        });
    });
  }

  /**
   * Function to get Program info using Program id
   * @param code
   */
  getProgram(code: string): Observable<Program[]> {
    return new Observable((observer: Observer<Program[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/programs?code=' + code + '&isPublished=true', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe((rArray) => {
          let programs: Program[] = rArray.map(rItem => {
            return {
              id: rItem.id,
              name: rItem.name,
              uri: rItem.uri,
              code: rItem.code,
              client: rItem.client
            }
          });
          observer.next(programs);
          observer.complete();
        });
    });
  }

  /**
   * Function to get exam info using Exam id
   * @param code
   */
  getExam(code: string, programId?: string): Observable<Exam[]> {
    return new Observable((observer: Observer<Exam[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/exams?code=' + code + '&programId=' + programId + '&isPublished=true', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe({
          next: (rArray) => {
            let exams: Exam[] = rArray.map(rItem => {
              return Exam.convertAPIObjectToExam(rItem);
            });
            exams.sort(ExamFeatureService.compareExamsForSort);
            observer.next(exams);
            observer.complete();
          },
          error: (err) => {
            observer.next(err);
            observer.complete();
          }
        });
    });
  }

  getPaymentFeeDetail(examId: any, countryCode: string): Observable<PaymentFeeDetail[]> {
    let paymentFeeDetails: PaymentFeeDetail[] = [];
    return new Observable((observer: Observer<RescheduleFee[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/exams/' + examId + '/country/' + countryCode + '/fees', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe((rNArray) => {
          if (rNArray.length === 0) {
            let paymentFeeDetail: PaymentFeeDetail = new PaymentFeeDetail();
            observer.next(paymentFeeDetails);
            observer.complete();
          }
          rNArray.forEach(payment => {
            this.httpService.get(payment.href, this.bootstrapParams.Token)
              .pipe(map(this.getFirstOrDefault))
              .subscribe((rArray) => {
                let paymentFeeDetail: PaymentFeeDetail = new PaymentFeeDetail();
                paymentFeeDetail.id = rArray.id;
                paymentFeeDetail.href = rArray.href;
                paymentFeeDetail.type = rArray.type;
                paymentFeeDetail.description = rArray.description;
                paymentFeeDetail.amount = rArray.amount;
                paymentFeeDetail.currency = rArray.currency;
                paymentFeeDetails.push(paymentFeeDetail);
                if (paymentFeeDetails.length == rNArray.length) {
                  observer.next(paymentFeeDetails);
                  observer.complete();
                }
              });
          });
        });
    });
  }

  // Get payment on the basis of booking id and exam id
  getBookingPaymentFeeDetail(apptId: any, examId: any): Observable<PaymentFeeDetail[]> {
    let paymentFeeDetails: PaymentFeeDetail[] = [];
    return new Observable((observer: Observer<RescheduleFee[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v2/bookings/' + apptId + '/exams/' + examId + '/charge', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe((rNArray: any) => {
          if (rNArray.length === 0) {
            observer.next(paymentFeeDetails);
            observer.complete();
          }
          rNArray.forEach((result: any) => {
            let paymentFeeDetail: PaymentFeeDetail = new PaymentFeeDetail();
            paymentFeeDetail.type = result.type;
            paymentFeeDetail.amount = result.amount;
            paymentFeeDetail.currency = result.currency;
            paymentFeeDetails.push(paymentFeeDetail);
            if (paymentFeeDetails.length == rNArray.length) {
              observer.next(paymentFeeDetails);
              observer.complete();
            }
          });
        });
    });
  }


  // Get payment refund on the basis of booking id and exam id
  getBookingPaymentRefundDetail(bookingId: any, examId: any): Observable<PaymentFeeDetail[]> {
    let paymentFeeDetails: PaymentFeeDetail[] = [];
    return new Observable((observer: Observer<RescheduleFee[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/bookings/' + bookingId + '/exams/' + examId + '/refund', this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe({
          next: (rNArray: any) => {
            if (rNArray.length === 0) {
              let paymentFeeDetail: PaymentFeeDetail = new PaymentFeeDetail();
              observer.next(paymentFeeDetails);
              observer.complete();
            }
            rNArray.forEach((result: any) => {
              let paymentFeeDetail: PaymentFeeDetail = new PaymentFeeDetail();
              paymentFeeDetail.id = result.exam.id;
              paymentFeeDetail.href = result.exam.href;
              paymentFeeDetail.type = result.refund.type;
              paymentFeeDetail.amount = result.refund.amount;
              paymentFeeDetail.currency = result.refund.currency;
              paymentFeeDetails.push(paymentFeeDetail);
              if (paymentFeeDetails.length == rNArray.length) {
                observer.next(paymentFeeDetails);
                observer.complete();
              }
            });
          },
          error: (err) => {
            observer.next(err);
            observer.complete();
          }
        });
    });
  }

  //Get consents on basis of booking id
  getConsents(bookingId: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/bookings/' + bookingId + '/consents', this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((results) => {
          observer.next(results);
          observer.complete();
        });
    });
  }

  reScheduleBookingWithoutPayment(
    reScheduleBookingRequestData: any,
    examDetailsForSettlement?: any,
    metricDetails?: MetricDetails[],
    isBackToBackRequired?: boolean
  ): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      let msgResp = { message: "reconfirmationComponent.tryLater", status: false, confirmationNumber: "Error", response: "" };
      LanguageCultureCodes.forEach((lang) => {
        if (lang.key === this.selectedExamDetailService.selectedLanguageCode) {
          reScheduleBookingRequestData['culture'] = lang.culture;
        }
      });

      reScheduleBookingRequestData['sendConfirmation'] = true; //true value to send to booking 
      // TODO: Convert this into FlatMap in order to create just one observable.
      if (reScheduleBookingRequestData['tests'][0] && reScheduleBookingRequestData['tests'][0]["sessions"][0].hold.id) {
        this.validateHold(reScheduleBookingRequestData['tests'][0]["sessions"][0].hold.id).subscribe(response => {
          if (response) {
            this.httpService.put(this.bootstrapParams.rootApiUrl + 'api/v1/bookings', reScheduleBookingRequestData, this.bootstrapParams.Token)
              .pipe(map(this.getFirstOrDefault))
              .subscribe({
                next: (rNArray: any) => {
                  if (rNArray != undefined && rNArray != null) {
                    if (rNArray.tests.length > 0) {
                      if (rNArray.tests[0].sessions[0] != null && rNArray.tests[0].sessions[0] != undefined) {
                        msgResp = { message: "reconfirmationComponent.appointmentConfirmationMessage", status: true, confirmationNumber: rNArray.tests[0].sessions[0].confirmationNumber, response: rNArray };

                        // checking for length of metrics as no metrics are collected without google maps
                        if (metricDetails && metricDetails.length) {
                          this.metricApiService.postMetricDetails(metricDetails[0], reScheduleBookingRequestData.id, "ReSchedule");
                        }

                        if (this.selectedExamDetailService.isBiometricConsentCheckedReschedule) {
                          this.postBiometrics(rNArray.tests).subscribe(() => { })
                        }

                      }
                    }
                    observer.next(msgResp); // Need to Comment if un-commenting settle payment call in above section.
                    observer.complete(); // Need to Comment if un-commenting settle payment call in above section.
                  }
                },
                error: (err) => {
                  msgResp = {
                    message: "schedulePaymentComponent.errorMessage",
                    status: false,
                    confirmationNumber: "Error", response: ""
                  }
                  observer.next(msgResp);
                  observer.complete();
                }
              });
          } else {
            const holdId = reScheduleBookingRequestData['tests'][0]["sessions"][0].hold.id;
            this.metricApiService.log(LogLevel.Error, `Validate hold call returned expired when checking for hold ${holdId}`);
            this.timerService.expireTimer();
          }
        })
      }
    });
  }

  cancelBookingWithoutPayment(
    appointmentIds: any,
    examAccommodationsForNativeDelete: any[],
    refundDetails?: any,
    voucherPayment?: Payment[],
    confirmationNumber?: string
  ): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      let msgResp = { message: "cancellationConfirmationComponent.tryLater", status: false, confirmationNumber: "Error", response: "" };
      let bookingResp: any = null;

      if (
        !this.bootstrapParams.legacyCancel
        && this.selectedExamDetailService.isRefundRequired
        && refundDetails
        && refundDetails["transactionId"]
        && refundDetails["amount"] > 0
        // to keep consistent with USRWEB we are not refunding payments when we unburn a voucher in native cancel
        && !this.selectedExamDetailService.voucherTransactionId
      ) {
        // CC
        if (QrPaymentCodes.indexOf(refundDetails['paymentType'].toLowerCase()) === -1) {
          this.paymentService.confirmRefund(refundDetails, refundDetails["transactionId"])
            .pipe(mergeMap((confirmResp) => {
              if (confirmResp) {
                return this.deleteBooking(appointmentIds, examAccommodationsForNativeDelete);
              }
              return throwError(() => new Error('Confirm Refund'));
            }),
            catchError((err) => {
              if (err.message === 'Confirm Refund') {
                return throwError(() => err);
              }
              return throwError(() => new Error('Delete Booking'));
            }),
            mergeMap((response: any) => {
              if (!!response && response.status) {
                bookingResp = response;
                return this.paymentService.postRefundAmount(refundDetails);
              }
              return throwError(() => new Error('Delete Booking'));
            }),
            catchError((err) => {
              if (err.message === 'Confirm Refund' || err.message === 'Delete Booking') {
                return throwError(() => err);
              }
              this.metricApiService.log(LogLevel.Error, `CC Refund call failure. Booking successfully cancelled.` + `\nConfirmation Number ${confirmationNumber}`);
            }))
            .subscribe({
              next: (resp) => {
                observer.next(bookingResp);
                observer.complete();
              },
              error: (err) => {
                this.metricApiService.log(LogLevel.Error, `CC ${err} call failure` + `\nConfirmation Number ${confirmationNumber}`);
                observer.next(msgResp);
                observer.complete();
              }
            });
        }
        // QR
        else {
          this.paymentService.confirmRefund(QrRefund.convertPaymentToRefundConfirmation(refundDetails.transaction), refundDetails['transactionId'])
            .pipe(mergeMap((confirmResp) => {
              if (confirmResp) {
                return this.deleteBooking(appointmentIds, examAccommodationsForNativeDelete);
              }
              return throwError(() => new Error('Confirm Refund'));
            }),
            catchError((err) => {
              if (err.message === 'Confirm Refund') {
                return throwError(() => err);
              }
              return throwError(() => new Error('Delete Booking'));
            }),
            mergeMap((response: any) => {
              if (!!response && response.status) {
                bookingResp = response;
                return this.paymentService.refundQrCancel(refundDetails['transactionId'], refundDetails.transaction);
              }
              return throwError(() => new Error('Delete Booking'));
            }),
            catchError((err) => {
              if (err.message === 'Confirm Refund' || err.message === 'Delete Booking') {
                return throwError(() => err);
              }
              this.metricApiService.log(LogLevel.Error, `QR Refund call failure. Booking successfully cancelled.` + `\nConfirmation Number ${confirmationNumber}`);
            }))
            .subscribe({
              next: () => {
                observer.next(bookingResp);
                observer.complete();
              },
              error: (err) => {
                this.metricApiService.log(LogLevel.Error, `QR ${err} call failure` + `\nConfirmation Number ${confirmationNumber}`);
                observer.next(msgResp);
                observer.complete();
              }
            });
        }
      } else {
        this.deleteBooking(appointmentIds, examAccommodationsForNativeDelete).subscribe({
          next: (response: any) => {
            if (response && response.status) {
              if (voucherPayment && voucherPayment.length) {
                this.unredeemVoucher(voucherPayment, confirmationNumber).subscribe(() => { })
              }
              observer.next(response);
              observer.complete();
            } else {
              this.metricApiService.log(LogLevel.Error, `Delete Booking call failure` + `\nConfirmation Number ${confirmationNumber}`);
              observer.next(msgResp);
              observer.complete();
            }
          },
          error: (err) => {
            this.metricApiService.log(LogLevel.Error, `Delete Booking call failure` + `\nConfirmation Number ${confirmationNumber}`);
            observer.next(msgResp);
            observer.complete();
          }
        });
      }
    });
  }

  deleteBooking(
    appointmentIds: string[],
    examAccommodationsForNativeDelete: any[],
    transactionId?: string
  ) {
    let msgResp = {
      message: "errors.thereWasAnIssueRefund",
      status: false,
      confirmationNumber: "Error",
      response: "",
      errorId: SchedulingErrors.BookingError
    };
    return new Observable((observer: Observer<any>) => {
      let transactionQuery = '';
      if (!!transactionId) {
        transactionQuery = '&transactionids=' + transactionId;
      }
      if (!this.bootstrapParams.legacyCancel) {
        this.httpService.delete(this.bootstrapParams.rootApiUrl + 'api/v2/bookings?ids= [' + appointmentIds + ']' + transactionQuery, this.bootstrapParams.Token, examAccommodationsForNativeDelete)
          .pipe(map(this.getFirstOrDefault))
          .subscribe({
            next: (rNArray) => {
              if (rNArray != undefined && rNArray != null) {
                if (rNArray.tests.length > 0) {
                  if (rNArray.tests[0].sessions[0] != null && rNArray.tests[0].sessions[0] != undefined) {
                    msgResp = {
                      message: "cancellationConfirmationComponent.helpText",
                      status: true,
                      confirmationNumber: rNArray.tests[0].sessions[0].confirmationNumber,
                      response: rNArray,
                      errorId: ""
                    };
                  }
                }
                observer.next(msgResp);
                observer.complete();
              } else {
                msgResp = {
                  message: "cancellationConfirmationComponent.helpText",
                  status: true,
                  confirmationNumber: rNArray.tests[0].sessions[0].confirmationNumber,
                  response: rNArray,
                  errorId: ""
                };
                observer.next(msgResp);
                observer.complete();
              }
            },
            error: (err) => {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.BookingError
              };
              observer.error('Booking Error');
              observer.complete();
            }
          });
      } else {
        if (!!transactionId) {
          transactionQuery = '&transactionid=' + transactionId;
        } else {
          transactionQuery = '';
        }
        this.httpService.delete(this.bootstrapParams.rootApiUrl + 'api/v1/bookings?ids=[' + appointmentIds + ']' + transactionQuery, this.bootstrapParams.Token)
          .pipe(map(this.getFirstOrDefault))
          .subscribe({
            next: (rNArray) => {
              if (rNArray != undefined && rNArray != null) {
                if (rNArray.tests.length > 0) {
                  if (rNArray.tests[0].sessions[0] != null && rNArray.tests[0].sessions[0] != undefined) {
                    msgResp = {
                      message: "cancellationConfirmationComponent.helpText",
                      status: true,
                      confirmationNumber: rNArray.tests[0].sessions[0].confirmationNumber,
                      response: rNArray,
                      errorId: ""
                    };
                  }
                  observer.next(msgResp);
                  observer.complete();
                }
                else {
                  observer.next(msgResp);
                  observer.complete();
                }
              }
            },
            error: (err) => {
              msgResp = {
                message: "errors.thereWasAnIssueRefund",
                status: false,
                confirmationNumber: "Error",
                response: "",
                errorId: SchedulingErrors.BookingError
              };
              observer.error('Booking Error');
              observer.complete();
            }
          });
      }
    });
  }

  unredeemVoucher(voucherPayment: Payment[], confirmationNumber: string) {
    if (this.bootstrapParams.legacyCancel) {
      return new Observable((observer: Observer<any>) => {
        observer.next(true);
        observer.complete();
      })
    }
    return new Observable((observer: Observer<any>) => {
      this.httpService.put(this.bootstrapParams.PaymentRootApiUrl + 'api/v1/payments/refunds/voucher/' + voucherPayment[0].transactionId, '"' + confirmationNumber + '"', this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((rNArray) => {
          if (rNArray != undefined && rNArray != null) {
            observer.next(true);
            observer.complete();
          }
        })
    })
  }

  getApplicableTAList(examSetWithTA: any): any[] {
    let examPostTestingAccomodations: any[] = [];
    if (examSetWithTA.length) {
      examSetWithTA.forEach((examSet: any) => {
        let applicableTAList: any[] = [];
        if (examSet.testingAccommodations != null && examSet.testingAccommodations != undefined && examSet.testingAccommodations.length) {
          examSet.testingAccommodations.forEach((testAccom: any) => {
            applicableTAList.push({ name: testAccom.name, attributes: testAccom.attributes });
          });
        }
        examPostTestingAccomodations.push({ id: examSet.id, testingAccommodations: applicableTAList });
      });
    }
    return examPostTestingAccomodations;
  }

  getTimezones(isProctor: any): Observable<Timezone[]> {
    return new Observable((observer: Observer<Timezone[]>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/timezones/' + isProctor, this.bootstrapParams.Token)
        .pipe(map(this.getResultArray))
        .subscribe({
          next: (rArray) => {
            let timezones: Timezone[] = rArray.map(rItem => {
              return new Timezone(
                rItem.timezoneid,
                rItem.code,
                rItem.name,
                rItem.isProctor,
                rItem.offset,
                rItem.systemName
              );
            });
            observer.next(timezones);
            observer.complete();
          },
          error: (err) => {
            observer.next(err);
            observer.complete();
          }
        });
    });
  }

  getProctors(startDate: string, endDate: string, eligibilityId: string, timezoneCode: string, examSetWithTA?: Exam[], takeSiteSlotsize?: number, skipSiteSlotsize?: number): Observable<any> {
    let baseSiteSlotUri: string = this.bootstrapParams.rootApiUrl + 'api/v1/proctors/?';
    let eligibilityIdUri: string = eligibilityId && eligibilityId !== '-1' ? '&eligibilityId=' + eligibilityId : '';
    let requestUri = baseSiteSlotUri + 'startDate=' + startDate +
      '&endDate=' + endDate +
      '&timezonecode=' + timezoneCode +
      '&Take=' + takeSiteSlotsize + '&skip=' + skipSiteSlotsize
      + eligibilityIdUri;

    return new Observable((observer: Observer<any>) => {
      let proctors: Proctor[] = [];
      this.httpService.search(
        requestUri, this.getApplicableTAList(examSetWithTA), this.bootstrapParams.Token, true)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (result: any) => {
            let itinerary = (
              !!result && !!result.itinerary && !!result.itinerary.length && !!result.itinerary[0].tests ?
                result.itinerary[0].tests :
                []
            );

            let resultSlots: any = result.suggestions.length > 0 ? result.suggestions[0].slots : [];
            resultSlots.forEach((resultSlot: any) => {
              let proctor = new Proctor(
                resultSlot.id,
                resultSlot.href,
                resultSlot.startDateTime,
                moment(resultSlot.startDateTime).format('YYYY-MM-DD'),
                this.utilityService.getTimeString(resultSlot.startDateTime, this.selectedExamDetailService.timeFormat),
                resultSlot.duration
              );
              proctors.push(proctor);
            });
            observer.next({
              proctors: proctors,
              itinerary: itinerary,
              error: ''
            });
            observer.complete();
          },
          error: (err) => {
            let errMessage = '';
            //Duration exceeds Battery MaxDuration
            if (err) {
              const errorType = err.error[EligibilityErrorCodeMap.AppointmentTooLong];
              errMessage = errorType && errorType.length ? errorType[0]['Description'] : '';
            }
            let proctors: Proctor[] = [];
            observer.next({
              proctors: proctors,
              itinerary: [],
              error: errMessage
            });
            observer.complete();
          }
        });
    });

  }

  getExamSetByExam(exam: Exam): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.get(this.bootstrapParams.rootApiUrl + 'api/v1/exams/' + exam.id + '/set/', this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe((examSet: any) => {
          this.selectedExamDetailService.examSetId = examSet.id;
          if (examSet.attributes) {
            if (!!examSet.attributes.b2b) {
              this.selectedExamDetailService.isBackToBackRequired = true;
              if (examSet.attributes.b2b.bufferTime > 0) {
                this.selectedExamDetailService.setBatteryConfig(
                  new BatteryConfig(examSet.attributes.b2b.bufferTime, examSet.attributes.b2b.maxDuration));
              }
            }
            if (examSet.attributes.requiredSchedule) {
              this.selectedExamDetailService.scheduleSetAttributeCode = true;
            }
            if (examSet.attributes.requiredReSchedule) {
              this.selectedExamDetailService.rescheduleSetAttributeCode = true;
            }
            if (examSet.attributes.requiredCancel) {
              this.selectedExamDetailService.cancelSetAttributeCode = true;
            }
          }
          observer.next({
            'isB2BRequired': this.selectedExamDetailService.isBackToBackRequired,
            'scheduleSetAttributeCode': this.selectedExamDetailService.scheduleSetAttributeCode,
            'rescheduleSetAttributeCode': this.selectedExamDetailService.rescheduleSetAttributeCode,
            'cancelSetAttributeCode': this.selectedExamDetailService.cancelSetAttributeCode,
          });
          observer.complete();
        });
    });
  }

  postBiometrics(tests: any): Observable<any> {

    return new Observable((observer: Observer<any>) => {
      tests.forEach((test: any) => {
        this.httpService.post(this.bootstrapParams.rootApiUrl + 'api/v1/bookings/' + test.sessions[0].id + '/consents', {
          "consent": {
            "Biometric": ['web']
          }
        }, this.bootstrapParams.Token)
          .pipe(map(this.getFirstOrDefault))
          .subscribe(() => {
            observer.next(null);
            observer.complete();
          });
      });
    });
  }

  private updateBooking(reScheduleBookingRequestData: any): Observable<any> {
    reScheduleBookingRequestData['sendConfirmation'] = true; // set a true value to sendEmail
    const holdId = reScheduleBookingRequestData['tests'][0]['sessions'][0].hold.id;
    this.metricApiService.log(LogLevel.Debug, `Attempting booking update.\nHold: ${holdId}`);
    return new Observable((observer: Observer<any>) => {
      this.httpService.put(this.bootstrapParams.rootApiUrl + 'api/v1/bookings', reScheduleBookingRequestData, this.bootstrapParams.Token)
        .pipe(map(this.getFirstOrDefault))
        .subscribe({
          next: (rNArray) => {
            this.metricApiService.log(LogLevel.Debug, `Booking update completed.\nHold: ${holdId}`);
            observer.next(rNArray);
            observer.complete();
          },
          error: (err) => {
            this.metricApiService.log(LogLevel.Debug, `Error updating booking: ${JSON.stringify(err)}\nHold: ${holdId}`);
            observer.error('booking failed!');
            observer.complete();
          }
        });
    });
  }
}
